import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import { Plus, X, Edit2, UserCog, Shield, Settings, Bell, Send } from 'lucide-react';
import { sendNotification } from '../lib/notifications';

const CATEGORIES = ['director','manager','office_admin','academic_counsellor','faculty','placement_officer','site_engineer'];

// ── MASTER SECTIONS LIST ─────────────────────────────────────────────────
// To add a new section: add entry here with key + label (+ optional subsections)
// It will automatically appear in Staff Permissions modal
// ─────────────────────────────────────────────────────────────────────────
const SECTIONS = [
  { key: 'dashboard',        label: 'Dashboard' },
  { key: 'students',         label: 'Student Profile', subsections: [
    { key: 'students_list',    label: 'Students' },
    { key: 'students_reports', label: 'Reports' },
  ]},
  { key: 'attendance',       label: 'Attendance', subsections: [
    { key: 'attendance_mark',          label: 'Mark Attendance' },
    { key: 'attendance_summary',       label: 'Attendance Summary' },
    { key: 'attendance_faculty',       label: 'Faculty Report' },
    { key: 'attendance_schedule',      label: 'Class Schedule' },
    { key: 'attendance_reports',       label: 'Reports' },
    { key: 'attendance_absent_tracker',label: 'Absent Tracker' },
    { key: 'attendance_lab',           label: 'Lab Report' },
  ]},
  { key: 'fees',             label: 'Fees' },
  { key: 'fee_schedules',    label: 'Fee Schedules', subsections: [
    { key: 'fee_schedules_view',     label: 'Schedules' },
    { key: 'fee_schedules_tracker',  label: 'Tracker' },
    { key: 'fee_schedules_plans',    label: 'Plans' },
    { key: 'fee_schedules_reports',  label: 'Reports' },
  ]},
  { key: 'assignments',      label: 'Assignments' },
  { key: 'language_training',label: 'Language Training' },
  { key: 'requests',         label: 'Student Requests' },
  { key: 'exams',            label: 'Examinations', subsections: [
    { key: 'exams_requests',    label: 'Exam Requests' },
    { key: 'exams_evaluation',  label: 'Evaluation' },
    { key: 'exams_analytics',   label: 'Analytics' },
    { key: 'exams_reexam',      label: 'Re-exam' },
    { key: 'exams_ranking',     label: 'Ranking' },
    { key: 'exams_mock',        label: 'Mock Interview' },
    { key: 'exams_interview',   label: 'Interview Scheduling' },
  ]},
  { key: 'certificates',     label: 'Certificates', subsections: [
    { key: 'cert_requests',     label: 'Certificate Requests' },
    { key: 'cert_distribution', label: 'Certificate Distribution' },
    { key: 'cert_all',          label: 'All Certificates' },
    { key: 'cert_pending',      label: 'Pending' },
    { key: 'cert_collected',    label: 'Collected' },
    { key: 'cert_processed',    label: 'Processed' },
  ]},
  { key: 'internship',       label: 'Internship' },
  { key: 'hostel',           label: 'Hostel' },
  { key: 'placement',        label: 'Placement Cell', subsections: [
    { key: 'placement_dashboard',      label: 'Dashboard' },
    { key: 'placement_followuptracker',label: 'Follow-up Tracker' },
    { key: 'placement_studentprofile', label: 'Student Placement Profile' },
    { key: 'placement_concerns',       label: 'Issues & Concerns' },
    { key: 'placement_hrstatus',       label: 'HR Status' },
    { key: 'placement_joboffers',      label: 'Job Offers' },
    { key: 'placement_placementstatus',label: 'Placement Status' },
    { key: 'placement_afterplacement', label: 'After Armstech Placement' },
    { key: 'placement_updates',        label: 'Updates' },
    { key: 'placement_assignments',    label: 'Assignments' },
    { key: 'placement_mybatches',      label: 'My Batches (coordinator)' },
    { key: 'placement_counselordash',  label: 'Counselor Dashboard' },
    { key: 'placement_companydb',      label: 'Company DB' },
    { key: 'placement_jobpostings',    label: 'Job Postings' },
    { key: 'placement_manpower',       label: 'Manpower DB' },
    { key: 'placement_consultancies',  label: 'Consultancy' },
    { key: 'placement_hr_dashboard',   label: 'HR Performance' },
    { key: 'placement_payments',       label: 'Payments' },
    { key: 'placement_report',         label: 'Report' },
  ]},
  { key: 'hr',               label: 'Armstech HR', subsections: [
    { key: 'hr_staff',         label: 'Staff Details' },
    { key: 'hr_attendance',    label: 'HR Attendance' },
    { key: 'hr_salary',        label: 'Salary' },
    { key: 'hr_leave',         label: 'Leave (CL & SL)' },
    { key: 'hr_advance',       label: 'Staff Advance' },
    { key: 'hr_esipf',         label: 'ESI & PF' },
    { key: 'hr_interview',     label: 'Staff Interview Status' },
    { key: 'hr_reports',       label: 'HR Reports' },
  ]},
  { key: 'crm',              label: 'Marketing CRM', subsections: [
    { key: 'crm_students',      label: 'Students' },
    { key: 'crm_calls',         label: 'Call Center' },
    { key: 'crm_reports',       label: 'Reports' },
    { key: 'crm_followups',     label: 'Follow-ups' },
    { key: 'crm_admin',         label: 'Admin' },
  ]},
  { key: 'nactet',           label: 'NACTET Scholarship', subsections: [
    { key: 'nactet_dashboard',   label: 'Dashboard' },
    { key: 'nactet_colleges',    label: 'Colleges' },
    { key: 'nactet_programs',    label: 'Programs' },
    { key: 'nactet_students',    label: 'Students' },
    { key: 'nactet_winners',     label: 'Winners' },
    { key: 'nactet_followup',    label: 'Follow-up' },
    { key: 'nactet_reports',     label: 'Reports' },
  ]},
  { key: 'accounts',         label: 'Accounts', subsections: [
    { key: 'accounts_armstech',    label: 'ARMSTECH' },
    { key: 'accounts_slccs',       label: 'SLCCS' },
    { key: 'accounts_construction',label: 'Construction' },
    { key: 'accounts_cashbook',    label: 'Cash Book' },
    { key: 'accounts_entries',     label: 'Entries' },
    { key: 'accounts_summary',     label: 'Summary' },
    { key: 'accounts_balances',    label: 'Balances' },
    { key: 'accounts_report',      label: 'Financial Report' },
    { key: 'accounts_party_ledger',label: 'Party Ledger' },
    { key: 'accounts_reminders',   label: 'Reminders & Payments' },
    { key: 'accounts_reconciliation', label: 'Bank Reconciliation' },
    { key: 'expense_inbox',        label: 'Expense Inbox' },
    { key: 'petty_cash',           label: 'Petty Cash' },
  ]},
  { key: 'gst',              label: 'GST Purchase & Sales', subsections: [
    { key: 'gst_sales',       label: 'Sale Bills' },
    { key: 'gst_purchase',    label: 'Purchase Bills' },
  ]},
  { key: 'civil',            label: 'Civil Construction', subsections: [
    { key: 'civil_projects',  label: 'Projects DB' },
    { key: 'civil_tenders',   label: 'Tender DB' },
  ]},
  { key: 'assets', label: 'Assets & Consumables', subsections: [
    { key: 'assets_dashboard',   label: 'Dashboard' },
    { key: 'assets_assets',      label: 'Assets' },
    { key: 'assets_consumables', label: 'Consumables' },
    { key: 'assets_vendors',     label: 'Vendors' },
    { key: 'assets_settings',    label: 'Settings (manager)' },
  ]},
  { key: 'applications',     label: 'Applications' },
  { key: 'concerns',         label: 'Concerns & Requests' },
  { key: 'master_data',      label: 'Master Data' },
  { key: 'staff_admin',      label: 'Staff & Users' },
  { key: 'activity_log',     label: 'Activity Log' },
  { key: 'expenses',         label: 'My Expenses' },
];

function sortBatches(batches) {
  // Ongoing first, then Upcoming, then Completed; inside each group the newest batch (latest start date) comes first.
  const rank = s => { const v = String(s || '').trim().toLowerCase(); return (v === 'ongoing' || v === 'active' || v === 'running') ? 0 : (v === 'completed' || v === 'closed') ? 2 : 1; };
  return [...(batches || [])].sort((a, b) => {
    const d = rank(a.status) - rank(b.status);
    if (d) return d;
    const sa = a.start_date || '', sb = b.start_date || '';
    if (sa !== sb) return sa < sb ? 1 : -1;
    return (b.batch_name || '').localeCompare(a.batch_name || '', undefined, { numeric: true });
  });
}

// Find the hr_staff row that belongs to a login (matched by email). Returns id or null.
async function findHrStaffId(email) {
  const em = (email || '').trim();
  if (!em) return null;
  const q = `"${em.replace(/"/g, '')}"`;
  const { data, error } = await supabase.from('hr_staff').select('id')
    .or(`personal_email.eq.${q},official_email.eq.${q}`).limit(1);
  if (error) return null;
  return data?.[0]?.id || null;
}

function ProjectAccessSection({
  staffId }) {
  const [projects, setProjects] = useState([]);
  const [assigned, setAssigned] = useState([]);

  useEffect(()=>{
    if (!staffId) return;
    supabase.from('construction_projects').select('id,project_name').eq('status','active').order('project_name')
      .then(({data})=>setProjects(data||[]));
    supabase.from('staff_project_access').select('project_name').eq('staff_id',staffId)
      .then(({data})=>setAssigned((data||[]).map(d=>d.project_name)));
  },[staffId]);

  async function toggle(projectName, checked) {
    if (checked) {
      const { error } = await supabase.from('staff_project_access').insert({staff_id:staffId, project_name:projectName});
      if (error) { toast.error(error.message); return; }
      setAssigned(prev=>[...prev,projectName]);
    } else {
      const { error } = await supabase.from('staff_project_access').delete().eq('staff_id',staffId).eq('project_name',projectName);
      if (error) { toast.error(error.message); return; }
      setAssigned(prev=>prev.filter(p=>p!==projectName));
    }
  }

  if (!projects.length) return <div style={{fontSize:12,color:'var(--text3)'}}>No active projects</div>;
  return (
    <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
      {projects.map(p=>(
        <label key={p.id} style={{display:'flex',alignItems:'center',gap:4,padding:'4px 10px',
          borderRadius:6,border:'1px solid var(--border)',cursor:'pointer',fontSize:12,
          background:assigned.includes(p.project_name)?'var(--accent-light)':'#fff',
          color:assigned.includes(p.project_name)?'var(--accent)':'var(--text)'}}>
          <input type="checkbox" checked={assigned.includes(p.project_name)}
            onChange={e=>toggle(p.project_name,e.target.checked)} style={{width:12,height:12}}/>
          {p.project_name}
        </label>
      ))}
    </div>
  );
}

export default function StaffAdmin() {
  const { canEdit, canView, staff: currentStaff, refreshStaff } = useAuth();
  const [staffList, setStaffList]         = useState([]);
  const [loading, setLoading]             = useState(true);
  const [showModal, setShowModal]         = useState(false);
  const [showPermModal, setShowPermModal] = useState(null);
  const [expenseCats, setExpenseCats] = useState([]); // assigned categories for current staff
  const [allExpenseCatsByType, setAllExpenseCatsByType] = useState({});
  useEffect(()=>{
    supabase.from('accounts_categories').select('name,category_type').order('name')
      .then(({data})=>{
        const byType = {};
        (data||[]).forEach(c=>{ const t=c.category_type||'General'; if(!byType[t]) byType[t]=[]; byType[t].push(c.name); });
        setAllExpenseCatsByType(byType);
      });
  },[]);
  const ALL_EXPENSE_CATS = Object.values(allExpenseCatsByType).flat();
  const [batches, setBatches] = useState([]);
  const [showCompletedBatches, setShowCompletedBatches] = useState(false);
  const [batchAssignments, setBatchAssignments] = useState({}); // staffId -> [batch_id,...]
  const [batchAssignLoading, setBatchAssignLoading] = useState(false);

  useEffect(()=>{
    supabase.from('batches').select('id,batch_name,discipline,status, start_date').order('batch_name')
      .then(({data})=>setBatches(sortBatches(data||[])));
  },[]);

  async function loadBatchAssignments(staffId) {
    // Find hr_staff id by matching email
    const staffMember = staffList.find(s=>s.id===staffId);
    if (!staffMember) return;
    const hrId = await findHrStaffId(staffMember.email);
    if (!hrId) { setBatchAssignments(prev=>({...prev,[staffId]:[]})); return; }
    const {data:assignments} = await supabase.from('batch_assignments')
      .select('batch_id').eq('staff_id', hrId);
    setBatchAssignments(prev=>({...prev,[staffId]:(assignments||[]).map(a=>a.batch_id)}));
    return hrId;
  }

  async function toggleBatchAssignment(staffId, batchId) {
    const staffMember = staffList.find(s=>s.id===staffId);
    if (!staffMember) return;
    setBatchAssignLoading(true);
    try {
      const hrId = await findHrStaffId(staffMember.email);
      if (!hrId) { toast.error('Staff not found in HR records (HR email must match the login email)'); return; }
      const current = batchAssignments[staffId]||[];
      if (current.includes(batchId)) {
        const { error } = await supabase.from('batch_assignments').delete().eq('staff_id',hrId).eq('batch_id',batchId);
        if (error) { toast.error(error.message); return; }
        setBatchAssignments(prev=>({...prev,[staffId]:(prev[staffId]||[]).filter(b=>b!==batchId)}));
        toast.success('Batch unassigned');
      } else {
        const { error } = await supabase.from('batch_assignments').insert({staff_id:hrId,batch_id:batchId});
        if (error) { toast.error(error.message); return; }
        setBatchAssignments(prev=>({...prev,[staffId]:[...(prev[staffId]||[]),batchId]}));
        toast.success('Batch assigned');
      }
    } finally {
      setBatchAssignLoading(false);
    }
  } // staff object
  const [editing, setEditing]             = useState(null);
  const [saving, setSaving]               = useState(false);
  const [permSaving, setPermSaving]       = useState(false);
  const [permissions, setPermissions]     = useState({});
  const permissionsRef = React.useRef({});

  const [showNotifModal, setShowNotifModal] = useState(false);
  const [showProjPermModal, setShowProjPermModal] = useState(null);
  const [projPerms, setProjPerms] = useState([]);
  const [projects, setProjects] = useState([]);
  const [notifForm, setNotifForm] = useState({ title: '', message: '', type: 'info', link: '' });
  const [notifSaving, setNotifSaving] = useState(false);
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', role: 'commentator', staff_category: 'faculty', is_active: true });

  useEffect(() => { loadStaff(); loadProjects(); }, []);

  // Real-time updates (debounced)
  useEffect(() => {
    let _rt = null;
    const _reload = () => { clearTimeout(_rt); _rt = setTimeout(() => { try { loadStaff(); } catch(e){} }, 800); };
    const ch = supabase.channel('staffadmin-realtime')
      .on('postgres_changes', {event:'*', schema:'public', table:'hr_staff'}, () => _reload())
      .on('postgres_changes', {event:'*', schema:'public', table:'batch_assignments'}, () => _reload())
      .subscribe();
    return () => { clearTimeout(_rt); supabase.removeChannel(ch); };
  }, []);

  async function loadProjects() {
    const { data } = await supabase.from('construction_projects').select('*').order('project_name');
    setProjects(data || []);
  }
  async function loadProjPerms(staffId) {
    const { data } = await supabase.from('staff_project_permissions').select('*').eq('staff_id', staffId);
    setProjPerms(data || []);
  }
  async function saveProjPerm(staffId, projectId, canArmstech, canSlccs) {
    const existing = projPerms.find(p => p.project_id === projectId);
    const r = existing
      ? await supabase.from('staff_project_permissions').update({can_view_armstech:canArmstech,can_view_slccs:canSlccs}).eq('id',existing.id)
      : await supabase.from('staff_project_permissions').insert({staff_id:staffId,project_id:projectId,can_view_armstech:canArmstech,can_view_slccs:canSlccs});
    if (r.error) toast.error(r.error.message);
    loadProjPerms(staffId);
  }
  async function removeProjPerm(id, staffId) {
    const { error } = await supabase.from('staff_project_permissions').delete().eq('id', id);
    if (error) toast.error(error.message);
    loadProjPerms(staffId);
  }

  async function loadStaff() {
    const { data, error } = await supabase.from('staff').select('*').order('full_name');
    if (error) toast.error('Could not load staff: ' + error.message);
    else setStaffList(data || []);
    setLoading(false);
  }

  async function sendNotif() {
    if (!notifForm.title.trim()) { toast.error('Title is required'); return; }
    setNotifSaving(true);
    const ok = await sendNotification({
      title: notifForm.title,
      message: notifForm.message,
      type: notifForm.type,
      link: notifForm.link || null,
      createdBy: currentStaff?.id,
    });
    if (ok) {
      toast.success('Notification sent to all staff ✅');
      setShowNotifModal(false);
      setNotifForm({ title: '', message: '', type: 'info', link: '' });
    } else {
      toast.error('Failed to send notification');
    }
    setNotifSaving(false);
  }

  async function save() {
    const name = (form.full_name || '').trim();
    const email = (form.email || '').trim().toLowerCase();
    if (!name || !email) { toast.error('Name and email are required'); return; }
    if (staffList.some(x => (x.email || '').trim().toLowerCase() === email && x.id !== editing)) { toast.error('A staff member with this email already exists'); return; }
    const editorCount = staffList.filter(s => s.role === 'editor' && s.id !== editing).length;
    if (form.role === 'editor' && editorCount >= 50) { toast.error('Maximum 50 editors allowed'); return; }
    setSaving(true);
    const { error } = editing
      ? await supabase.from('staff').update({ ...form, full_name: name, email }).eq('id', editing)
      : await supabase.from('staff').insert({ ...form, full_name: name, email });
    if (error) toast.error(error.message);
    else { toast.success(editing ? 'Staff updated' : 'Staff added'); setShowModal(false); loadStaff(); }
    setSaving(false);
  }

  function openEdit(s) {
    setEditing(s.id);
    setForm({ full_name: s.full_name, email: s.email, phone: s.phone || '', role: s.role, staff_category: s.staff_category, is_active: s.is_active });
    setShowModal(true);
  }

  function openNew() {
    setEditing(null);
    setForm({ full_name: '', email: '', phone: '', role: 'commentator', staff_category: 'faculty', is_active: true });
    setShowModal(true);
  }

  async function loadExpenseCats(staffId) {
    const { data } = await supabase.from('staff_expense_categories').select('category').eq('staff_id', staffId);
    setExpenseCats((data||[]).map(d=>d.category));
  }

  function openPermissions(s) {
    loadBatchAssignments(s.id);
    loadExpenseCats(s.id);
    const existing = s.permissions || {};
    const perms = {};
    SECTIONS.forEach(sec => {
      const val = existing[sec.key];
      if (val === 'hidden') perms[sec.key] = 'hidden';
      else if (val === 'view' || val === false) perms[sec.key] = 'view';
      else perms[sec.key] = 'full';
      // Also read subsection permissions
      if (sec.subsections) sec.subsections.forEach(sub => {
        const subVal = existing[sub.key];
        if (subVal === 'hidden') perms[sub.key] = 'hidden';
        else if (subVal === 'view' || subVal === false) perms[sub.key] = 'view';
        else if (subVal === 'full') perms[sub.key] = 'full';
        else perms[sub.key] = perms[sec.key]; // inherit from parent
      });
    });
    setPermissions(perms);
    permissionsRef.current = perms;
    setShowPermModal(s);
  }

  async function savePermissions() {
    if (!showPermModal) return;
    setPermSaving(true);
    const permsToSave = permissionsRef.current;
    const { data, error } = await supabase.from('staff').update({ permissions: permsToSave }).eq('id', showPermModal.id).select('id').single();
    if (error) { toast.error('Save failed: ' + error.message); }
    else {
      toast.success('Permissions updated for ' + showPermModal.full_name);
      // Update staffList immediately so modal shows new values next time
      setStaffList(prev => prev.map(s => s.id === showPermModal.id ? { ...s, permissions: permissionsRef.current } : s));
      setShowPermModal(null);
      loadStaff(); // also reload from DB to confirm
      if (currentStaff?.id === showPermModal.id) refreshStaff();
    }
    setPermSaving(false);
  }

  function toggleAll(value) {
    const perms = {};
    SECTIONS.forEach(s => {
      perms[s.key] = value;
      if (s.subsections) s.subsections.forEach(sub => { perms[sub.key] = value; });
    });
    setPermissions(perms);
    permissionsRef.current = perms;
  }

  function cyclePermission(key) {
    // Cycle: full → view → hidden → full
    const current = permissions[key] || 'full';
    const next = current === 'full' ? 'view' : current === 'view' ? 'hidden' : 'full';
    setPermissions(p => { const updated = { ...p, [key]: next }; permissionsRef.current = updated; return updated; });
  }

  async function toggleActive(id, val) {
    if (!val) {
      if (currentStaff?.id === id) { toast.error('You cannot deactivate your own account'); return; }
      if (!window.confirm('Deactivate this staff member? They will lose access to the ERP.')) return;
    }
    const { error } = await supabase.from('staff').update({ is_active: val }).eq('id', id);
    if (error) toast.error(error.message);
    else { toast.success(val ? 'Staff activated' : 'Staff deactivated'); loadStaff(); }
  }

  const editors = staffList.filter(s => s.role === 'editor');
  const commentators = staffList.filter(s => s.role === 'commentator');

  // Second line of defence (the route is also guarded in App.js): never show the staff list without view rights
  if (typeof canView === 'function' && !canView('staff_admin')) {
    return (
      <div className="page">
        <div className="empty-state"><h3>🚫 You don't have access to this section</h3></div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div><h1 className="page-title">Staff & Users</h1><p className="page-subtitle">Manage staff accounts, roles and permissions</p></div>
        <div style={{ display: 'flex', gap: 10 }}>
          {canEdit('staff_admin') && (
            <button className="btn btn-secondary" onClick={() => setShowNotifModal(true)}>
              <Bell size={14} /> Send Notification
            </button>
          )}
          {canEdit('staff_admin') && <button className="btn btn-primary" onClick={openNew}><Plus size={14} /> Add Staff</button>}
        </div>
      </div>

      {/* Role summary */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <div className="card" style={{ borderColor: 'var(--accent)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ width: 36, height: 36, background: 'var(--accent-light)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Shield size={18} color="var(--accent)" />
            </div>
            <div>
              <div style={{ fontWeight: 700 }}>Editors <span style={{ color: 'var(--accent)' }}>{editors.length}/50</span></div>
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>Full read/write access</div>
            </div>
          </div>
          <div className="progress-bar"><div className="progress-fill" style={{ width: `${(editors.length / 50) * 100}%`, background: 'var(--accent)' }} /></div>
          <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {editors.map(e => (
              <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--surface)', padding: '4px 10px', borderRadius: 20, fontSize: 12 }}>
                <div className="avatar" style={{ width: 20, height: 20, fontSize: 9 }}>{(e.full_name||'').split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}</div>
                {e.full_name}
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ width: 36, height: 36, background: 'var(--surface2)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <UserCog size={18} color="var(--text2)" />
            </div>
            <div>
              <div style={{ fontWeight: 700 }}>Commentators <span style={{ color: 'var(--text2)' }}>{commentators.length}</span></div>
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>Read-only access</div>
            </div>
          </div>
        </div>
      </div>

      {loading ? <div className="loading"><div className="spinner" /></div> : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Name</th><th>Email</th><th>Phone</th><th>Category</th><th>Role</th><th>Status</th><th>Linked Auth</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {staffList.length === 0 ? (
                <tr><td colSpan={8}><div className="empty-state"><UserCog /><h3>No staff records yet</h3><p>Add staff to link them with Supabase auth users</p></div></td></tr>
              ) : staffList.map(s => (
                <tr key={s.id} style={{ opacity: s.is_active ? 1 : 0.5 }}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className="avatar" style={{ width: 28, height: 28, fontSize: 11 }}>{(s.full_name||'').split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}</div>
                      <span style={{ fontWeight: 500 }}>{s.full_name}</span>
                    </div>
                  </td>
                  <td style={{ fontSize: 12 }}>{s.email}</td>
                  <td style={{ fontSize: 12 }}>{s.phone || '—'}</td>
                  <td><span className="badge badge-gray">{s.staff_category?.replace('_',' ')}</span></td>
                  <td><span className={`badge ${s.role === 'editor' ? 'badge-blue' : 'badge-gray'}`}>{s.role}</span></td>
                  <td><span className={`badge ${s.is_active ? 'badge-green' : 'badge-red'}`}>{s.is_active ? 'Active' : 'Inactive'}</span></td>
                  <td>
                    {s.user_id
                      ? <span className="badge badge-green">Linked</span>
                      : <span className="badge badge-amber">Not linked</span>
                    }
                  </td>
                  <td>
                    {canEdit('staff_admin') && (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(s)} title="Edit"><Edit2 size={13} /></button>
                        {s.role === 'editor' && (
                          <button className="btn btn-ghost btn-sm" onClick={() => openPermissions(s)} title="Manage section permissions" style={{ color: 'var(--accent)' }}>
                            <Settings size={13} />
                          </button>
                        )}
                        <button className="btn btn-ghost btn-sm" onClick={() => toggleActive(s.id, !s.is_active)} style={{ color: s.is_active ? 'var(--red)' : 'var(--green)', fontSize: 11 }}>
                          {s.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Setup instructions */}
      <div className="card" style={{ marginTop: 24, borderColor: 'var(--amber)' }}>
        <div className="card-title" style={{ color: 'var(--amber)' }}>⚠️ How to Link Staff with Supabase Auth</div>
        <ol style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 2, paddingLeft: 20 }}>
          <li>Go to <strong>Supabase Dashboard → Authentication → Users</strong></li>
          <li>Click <strong>"Invite User"</strong> and enter the staff member's email</li>
          <li>They'll receive an email to set their password</li>
          <li>After they sign in once, come back to this page and edit the staff record</li>
          <li>Run this SQL to link them: <code style={{ background: 'var(--surface)', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>UPDATE staff SET user_id = auth.uid() WHERE email = 'staff@email.com';</code></li>
          <li>Set <strong>role = 'editor'</strong> for staff who need write access (maximum 50 editors)</li>
        </ol>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Edit Staff' : 'Add Staff Member'}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group"><label>Full Name *</label><input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} /></div>
                <div className="form-group"><label>Email *</label><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></div>
                <div className="form-group"><label>Phone</label><input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></div>
                <div className="form-group"><label>Category</label>
                  <select value={form.staff_category} onChange={e => setForm(f => ({ ...f, staff_category: e.target.value }))}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c.replace('_',' ')}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Role</label>
                  <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                    <option value="commentator">Commentator (Read only)</option>
                    <option value="editor">Editor (Full access)</option>
                  </select>
                </div>
                <div className="form-group"><label>Status</label>
                  <select value={form.is_active ? 'active' : 'inactive'} onChange={e => setForm(f => ({ ...f, is_active: e.target.value === 'active' }))}>
                    <option value="active">Active</option><option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              {form.role === 'editor' && editors.length >= 50 && !editing && (
                <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--red-light)', border: '1px solid var(--red)', borderRadius: 8, fontSize: 13, color: 'var(--red)' }}>
                  ⚠️ Maximum 50 editors already assigned. Demote one editor first.
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : editing ? 'Update Staff' : 'Add Staff'}</button>
            </div>
          </div>
        </div>
      )}

      {/* PERMISSIONS MODAL */}
      {showPermModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowPermModal(null)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <span className="modal-title">
                Section Permissions — {showPermModal.full_name}
              </span>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowPermModal(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">

              {/* Batch Assignments */}
              <div style={{background:'var(--surface2)',borderRadius:8,padding:'12px 14px',marginBottom:16}}>
                <div style={{fontWeight:700,fontSize:13,marginBottom:8}}>📋 Batch Assignments <span style={{fontSize:11,color:'var(--text3)',fontWeight:400}}>(coordinator gets edit access for assigned batches only)</span></div>
                {showPermModal.role==='admin'
                  ? <div style={{fontSize:12,color:'var(--green)'}}>✅ Admin — has edit access on all batches</div>
                  : <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                      {(()=>{
                        // ongoing + upcoming always; completed batches on demand (already sorted newest first) or when already assigned to this person
                        const mine = batchAssignments[showPermModal.id]||[];
                        const active = batches.filter(b=>b.status==='ongoing'||b.status==='upcoming');
                        const done = batches.filter(b=>b.status!=='ongoing'&&b.status!=='upcoming')
                          .filter(b=>showCompletedBatches||mine.includes(b.id));
                        return [...active,...done];
                      })().map(b=>{
                        const assigned = (batchAssignments[showPermModal.id]||[]).includes(b.id);
                        return (
                          <button key={b.id}
                            className={`btn btn-sm ${assigned?'btn-primary':'btn-secondary'}`}
                            style={{fontSize:11,opacity:batchAssignLoading?0.6:1}}
                            onClick={()=>toggleBatchAssignment(showPermModal.id,b.id)}
                            disabled={batchAssignLoading}>
                            {assigned?'✓ ':''}{b.batch_name}
                            <span style={{fontSize:10,color:assigned?'rgba(255,255,255,0.7)':'var(--text3)',marginLeft:3}}>{b.discipline==='mechanical'?'M':'C'}</span>
                          </button>
                        );
                      })}
                      {batches.filter(b=>b.status==='ongoing'||b.status==='upcoming').length===0&&
                        <div style={{fontSize:12,color:'var(--text3)'}}>No active batches found</div>}
                    </div>
                }
                {showPermModal.role!=='admin'&&(
                  <label style={{display:'flex',alignItems:'center',gap:6,marginTop:8,fontSize:12,color:'var(--text2)',cursor:'pointer'}}>
                    <input type="checkbox" checked={showCompletedBatches} onChange={e=>setShowCompletedBatches(e.target.checked)}/>
                    Show completed batches too (EM43, EM44, TM22 …)
                  </label>
                )}
                {(batchAssignments[showPermModal.id]||[]).some(id=>batches.find(b=>b.id===id)?.status==='completed')&&(
                  <div style={{marginTop:8,fontSize:11,color:'var(--text3)'}}>
                    Also assigned to completed batches: {batches.filter(b=>b.status==='completed'&&(batchAssignments[showPermModal.id]||[]).includes(b.id)).map(b=>b.batch_name).join(', ')||'none'}
                  </div>
                )}
              </div>
              <div style={{ background: 'var(--accent-light)', border: '1px solid var(--accent)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
                Click each section to cycle through: <strong style={{ color: 'var(--green)' }}>✅ Full Access</strong> → <strong style={{ color: 'var(--amber)' }}>👁️ View Only</strong> → <strong style={{ color: 'var(--red)' }}>🚫 Hidden</strong>
              </div>

              {/* Quick actions */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => toggleAll('full')}>✅ Full Access All</button>
                <button className="btn btn-secondary btn-sm" onClick={() => toggleAll('view')}>👁️ View Only All</button>
                <button className="btn btn-secondary btn-sm" onClick={() => toggleAll('hidden')}>🚫 Hide All</button>
              </div>

              {/* Section cards */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {SECTIONS.map(sec => {
                  const level = permissions[sec.key] || 'full';
                  const cfgMap = {
                    full:   { label: '✅ Full', color: 'var(--green)',  bg: 'rgba(0,176,155,0.08)', border: 'var(--accent)' },
                    view:   { label: '👁️ View', color: 'var(--amber)',  bg: 'rgba(245,158,11,0.08)', border: 'var(--amber)' },
                    hidden: { label: '🚫 Hidden', color: 'var(--red)',  bg: 'rgba(239,68,68,0.08)',  border: 'var(--red)' },
                  };
                  const config = cfgMap[level];
                  return (
                    <div key={sec.key}>
                      {/* Main section row */}
                      <div onClick={() => cyclePermission(sec.key)}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 8, border: `1.5px solid ${config.border}`, background: config.bg, cursor: 'pointer', transition: 'all 0.15s', userSelect: 'none' }}>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{sec.label}</div>
                        <span style={{ fontSize: 12, fontWeight: 600, color: config.color }}>{config.label}</span>
                      </div>

                      {/* Subsections — indented */}
                      {sec.subsections && (
                        <div style={{ marginLeft: 20, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {sec.subsections.map(sub => {
                            const subLevel = permissions[sub.key] !== undefined ? permissions[sub.key] : (level === 'hidden' ? 'hidden' : level);
                            const subConfig = cfgMap[subLevel];
                            return (
                              <div key={sub.key} onClick={() => cyclePermission(sub.key)}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', borderRadius: 6, border: `1px solid ${subConfig.border}`, background: subConfig.bg, cursor: 'pointer', transition: 'all 0.15s', userSelect: 'none', opacity: level === 'hidden' ? 0.5 : 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{ color: 'var(--text3)', fontSize: 12 }}>└</span>
                                  <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text2)' }}>{sub.label}</span>
                                </div>
                                <span style={{ fontSize: 11, fontWeight: 600, color: subConfig.color }}>{subConfig.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Project Access */}
              <div style={{marginTop:16,padding:'12px 14px',background:'var(--surface2)',borderRadius:8,marginBottom:12}}>
                <div style={{fontWeight:600,fontSize:13,marginBottom:6}}>Project Access (Expenses)</div>
                <div style={{fontSize:12,color:'var(--text3)',marginBottom:8}}>Select which construction projects this staff can see in My Expenses</div>
                <ProjectAccessSection staffId={showPermModal?.id}/>
              </div>

              {/* Expense Categories */}
              <div style={{marginTop:16,padding:'12px 14px',background:'var(--surface2)',borderRadius:8}}>
                <div style={{fontWeight:600,fontSize:13,marginBottom:10}}>Expense Categories Access</div>
                <div style={{fontSize:12,color:'var(--text3)',marginBottom:8}}>Select which expense categories this staff can use in My Expenses</div>
                <div>
                  {Object.keys(allExpenseCatsByType).sort().map(type=>(
                    <div key={type} style={{marginBottom:10}}>
                      <div style={{fontSize:11,fontWeight:700,color:'var(--text3)',marginBottom:6,textTransform:'uppercase',letterSpacing:1}}>{type}</div>
                      <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                        {allExpenseCatsByType[type].map(cat=>(
                          <label key={cat} style={{display:'flex',alignItems:'center',gap:4,padding:'4px 10px',
                            borderRadius:6,border:'1px solid var(--border)',cursor:'pointer',fontSize:12,
                            background:expenseCats.includes(cat)?'var(--accent-light)':'#fff',
                            color:expenseCats.includes(cat)?'var(--accent)':'var(--text)'}}>
                            <input type="checkbox" checked={expenseCats.includes(cat)}
                              onChange={async e=>{
                                if (e.target.checked) {
                                  const { error } = await supabase.from('staff_expense_categories').insert({staff_id:showPermModal.id,category:cat});
                                  if (error) { toast.error(error.message); return; }
                                  setExpenseCats(prev=>[...prev,cat]);
                                } else {
                                  const { error } = await supabase.from('staff_expense_categories').delete()
                                    .eq('staff_id',showPermModal.id).eq('category',cat);
                                  if (error) { toast.error(error.message); return; }
                                  setExpenseCats(prev=>prev.filter(c=>c!==cat));
                                }
                              }} style={{width:12,height:12}}/>
                            {cat}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{marginTop:8,display:'flex',gap:8}}>
                  <button className="btn btn-ghost btn-sm" style={{fontSize:11}} onClick={async()=>{
                    const d1 = await supabase.from('staff_expense_categories').delete().eq('staff_id',showPermModal.id);
                    if (d1.error) { toast.error(d1.error.message); return; }
                    const i1 = await supabase.from('staff_expense_categories').insert([...new Set(ALL_EXPENSE_CATS)].map(cat=>({staff_id:showPermModal.id,category:cat})));
                    if (i1.error) { toast.error(i1.error.message); loadExpenseCats(showPermModal.id); return; }
                    setExpenseCats([...new Set(ALL_EXPENSE_CATS)]);
                  }}>Select All</button>
                  <button className="btn btn-ghost btn-sm" style={{fontSize:11}} onClick={async()=>{
                    const d1 = await supabase.from('staff_expense_categories').delete().eq('staff_id',showPermModal.id);
                    if (d1.error) { toast.error(d1.error.message); return; }
                    setExpenseCats([]);
                  }}>Clear All</button>
                </div>
              </div>

              {/* Legend */}
              <div style={{ marginTop: 16, padding: '10px 14px', background: 'var(--surface)', borderRadius: 8, fontSize: 12, color: 'var(--text3)', display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                <span><strong style={{ color: 'var(--green)' }}>✅ Full Access</strong> — Can view, add, edit and delete</span>
                <span><strong style={{ color: 'var(--amber)' }}>👁️ View Only</strong> — Can see but not edit anything</span>
                <span><strong style={{ color: 'var(--red)' }}>🚫 Hidden</strong> — Section removed from sidebar entirely</span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowPermModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={savePermissions} disabled={permSaving}>
                {permSaving ? 'Saving...' : 'Save Permissions'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SEND NOTIFICATION MODAL */}
      {showNotifModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowNotifModal(false)}>
          <div className="modal" style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <span className="modal-title">🔔 Send Notification to All Staff</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowNotifModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group full">
                  <label>Title *</label>
                  <input
                    value={notifForm.title}
                    onChange={e => setNotifForm(f => ({ ...f, title: e.target.value }))}
                    placeholder="e.g. Staff Meeting Tomorrow at 10am"
                  />
                </div>
                <div className="form-group full">
                  <label>Message (Optional)</label>
                  <textarea
                    value={notifForm.message}
                    onChange={e => setNotifForm(f => ({ ...f, message: e.target.value }))}
                    rows={3}
                    placeholder="Additional details..."
                  />
                </div>
                <div className="form-group">
                  <label>Type</label>
                  <select value={notifForm.type} onChange={e => setNotifForm(f => ({ ...f, type: e.target.value }))}>
                    <option value="info">ℹ️ Info</option>
                    <option value="warning">⚠️ Warning</option>
                    <option value="success">✅ Success</option>
                    <option value="error">🚨 Alert</option>
                    <option value="reminder">📅 Reminder</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Link (Optional)</label>
                  <select value={notifForm.link} onChange={e => setNotifForm(f => ({ ...f, link: e.target.value }))}>
                    <option value="">No link</option>
                    <option value="/">Dashboard</option>
                    <option value="/students">Students</option>
                    <option value="/attendance">Attendance</option>
                    <option value="/fees">Fees</option>
                    <option value="/exams">Examinations</option>
                    <option value="/certificates">Certificates</option>
                    <option value="/hostel">Hostel</option>
                    <option value="/placement">Placement</option>
                    <option value="/requests">Student Requests</option>
                  </select>
                </div>
              </div>

              {/* Preview */}
              {notifForm.title && (
                <div style={{ marginTop: 16, padding: '12px 14px', background: 'var(--bg3)', borderRadius: 8, border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 8, textTransform: 'uppercase' }}>Preview</div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <div style={{ fontSize: 24 }}>
                      {notifForm.type === 'info' ? 'ℹ️' : notifForm.type === 'warning' ? '⚠️' : notifForm.type === 'success' ? '✅' : notifForm.type === 'error' ? '🚨' : '📅'}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{notifForm.title}</div>
                      {notifForm.message && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{notifForm.message}</div>}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowNotifModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={sendNotif} disabled={notifSaving || !notifForm.title.trim()}>
                <Send size={13} /> {notifSaving ? 'Sending...' : 'Send to All Staff'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showProjPermModal&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowProjPermModal(null)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <h3>🏗️ Project Access — {showProjPermModal.full_name}</h3>
              <button className="modal-close" onClick={()=>setShowProjPermModal(null)}>×</button>
            </div>
            <div className="modal-body" style={{maxHeight:'65vh',overflowY:'auto'}}>
              <table>
                <thead><tr><th>Project</th><th>Location</th><th style={{textAlign:'center'}}>Access</th><th style={{textAlign:'center'}}>ARMSTECH</th><th style={{textAlign:'center'}}>SLCCS</th></tr></thead>
                <tbody>
                  {projects.map(proj=>{const perm=projPerms.find(p=>p.project_id===proj.id);return(
                    <tr key={proj.id}>
                      <td style={{fontWeight:600}}>{proj.project_name}</td>
                      <td style={{fontSize:12}}>{proj.location||'—'}</td>
                      <td style={{textAlign:'center'}}><input type="checkbox" checked={!!perm} onChange={async e=>{if(e.target.checked)await saveProjPerm(showProjPermModal.id,proj.id,false,false);else if(perm)await removeProjPerm(perm.id,showProjPermModal.id);}}/></td>
                      <td style={{textAlign:'center'}}><input type="checkbox" checked={perm?.can_view_armstech||false} disabled={!perm} onChange={async e=>perm&&await saveProjPerm(showProjPermModal.id,proj.id,e.target.checked,perm.can_view_slccs||false)}/></td>
                      <td style={{textAlign:'center'}}><input type="checkbox" checked={perm?.can_view_slccs||false} disabled={!perm} onChange={async e=>perm&&await saveProjPerm(showProjPermModal.id,proj.id,perm.can_view_armstech||false,e.target.checked)}/></td>
                    </tr>
                  );})}
                  {projects.length===0&&<tr><td colSpan={5} style={{textAlign:'center',color:'var(--text3)',padding:20}}>No projects yet</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={()=>setShowProjPermModal(null)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
